import { useEffect, useRef, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Route, Routes, useNavigate, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import "katex/dist/katex.min.css";
import * as api from "./api";

function ErrorMessage({ error }: { error: Error | null }) {
  if (!error) return null;
  return (
    <div className="notice" role="alert">
      {error.message}
      {error instanceof api.ApiError && error.legacyPath && (
        <p>
          <a href={api.legacyUrl(error.legacyPath)}>
            Tiếp tục trên trang tài khoản
          </a>
        </p>
      )}
    </div>
  );
}

function useSession() {
  return useQuery({
    queryKey: ["session"],
    queryFn: api.getSession,
    retry: false,
  });
}

export function App() {
  const session = useSession();
  const client = useQueryClient();
  const navigate = useNavigate();
  const signOut = useMutation({
    mutationFn: api.logout,
    onSuccess: (value) => {
      client.clear();
      client.setQueryData(["session"], value);
      navigate("/");
    },
  });
  return (
    <div className="shell">
      <header className="topbar">
        <Link className="brand" to="/">
          <span className="brand-mark">H</span>HDOJ
        </Link>
        <nav aria-label="Điều hướng chính">
          <Link to="/problems">Bài tập</Link>
          <Link to="/submissions">Bài đã nộp</Link>
          <a href={api.legacyUrl("/contests/")}>Kỳ thi</a>
        </nav>
        <div className="account">
          {session.data?.status === "authenticated" ? (
            <>
              <a
                className="user"
                href={api.legacyUrl(`/user/${session.data.user?.username}`)}
              >
                {session.data.user?.display_name || session.data.user?.username}
              </a>
              <button
                className="quiet"
                disabled={signOut.isPending}
                onClick={() => signOut.mutate()}
              >
                Đăng xuất
              </button>
            </>
          ) : (
            <Link className="button small" to="/login">
              Đăng nhập
            </Link>
          )}
        </div>
      </header>
      <ErrorMessage error={signOut.error} />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/problems" element={<Problems />} />
        <Route path="/problems/:code" element={<ProblemDetail />} />
        <Route path="/submissions" element={<Submissions />} />
        <Route path="/submissions/:id" element={<SubmissionDetail />} />
        <Route path="*" element={<LegacyPage />} />
      </Routes>
      <footer>
        <a href={api.legacyUrl("/")}>Trang cộng đồng và các chức năng khác</a>
      </footer>
    </div>
  );
}

function Home() {
  return (
    <main className="hero">
      <div className="hero-copy">
        <p className="eyebrow">ONLINE JUDGE · VIỆT NAM</p>
        <h1>
          Viết code.
          <br />
          <em>Giải quyết</em> vấn đề.
        </h1>
        <p className="lead">
          Luyện thuật toán, thử lời giải và theo dõi kết quả chấm bài của bạn.
        </p>
        <div className="hero-actions">
          <Link className="button" to="/problems">
            Khám phá bài tập →
          </Link>
          <Link className="text-button" to="/login">
            Tôi đã có tài khoản
          </Link>
        </div>
      </div>
      <div className="hero-card">
        <div className="terminal-head">
          <span />
          <span />
          <span />
          <small>solution.cpp</small>
        </div>
        <pre>
          <code>
            {
              "#include <iostream>\nusing namespace std;\n\nint main() {\n  int n; cin >> n;\n  cout << n * n;\n  return 0;\n}"
            }
          </code>
        </pre>
        <div className="pass">Mỗi bài toán, một cách nghĩ mới.</div>
      </div>
    </main>
  );
}

function Login() {
  const session = useSession();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const state = session.data;
  const complete = (value: api.Session) => {
    client.clear();
    client.setQueryData(["session"], value);
    setPassword("");
    setCode("");
    if (value.status === "authenticated") navigate("/problems");
  };
  const auth = useMutation({
    mutationFn: () =>
      state?.challenge && state.status === "pending_2fa"
        ? api.verifyTotp(state.challenge, code)
        : api.login(username, password),
    onSuccess: complete,
  });
  const reset = useMutation({ mutationFn: api.logout, onSuccess: complete });
  const restricted =
    state &&
    !["anonymous", "authenticated"].includes(state.status) &&
    !state.challenge;
  return (
    <main className="auth">
      <div className="auth-panel">
        <p className="eyebrow">CHÀO MỪNG TRỞ LẠI</p>
        <h1>Đăng nhập HDOJ</h1>
        {state?.status === "authenticated" ? (
          <Link to="/problems">Tiếp tục luyện tập →</Link>
        ) : restricted ? (
          <>
            <p>Hoàn tất xác minh tài khoản trước khi tiếp tục.</p>
            {state.legacy_url && (
              <a href={api.legacyUrl(state.legacy_url)}>
                Mở trang xác minh tài khoản
              </a>
            )}
            <button className="quiet" onClick={() => reset.mutate()}>
              Đăng nhập lại
            </button>
          </>
        ) : (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (!auth.isPending) auth.mutate();
            }}
          >
            {state?.challenge ? (
              <>
                <p>Nhập mã từ ứng dụng xác thực hoặc mã khôi phục.</p>
                <label>
                  Mã xác thực
                  <input
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                  />
                </label>
                <button
                  type="button"
                  className="quiet"
                  onClick={() => reset.mutate()}
                >
                  Bắt đầu lại
                </button>
              </>
            ) : (
              <>
                <label>
                  Tên đăng nhập
                  <input
                    autoComplete="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                  />
                </label>
                <label>
                  Mật khẩu
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </label>
              </>
            )}
            <button
              className="button wide"
              disabled={auth.isPending || reset.isPending}
            >
              {auth.isPending
                ? "Đang kiểm tra..."
                : state?.challenge
                  ? "Xác minh"
                  : "Đăng nhập"}
            </button>
          </form>
        )}
        <ErrorMessage error={auth.error || reset.error || session.error} />
        <div className="auth-links">
          <a href={api.legacyUrl("/accounts/register/")}>Tạo tài khoản</a>
          <a href={api.legacyUrl("/accounts/password/reset/")}>Quên mật khẩu</a>
          <a href={api.legacyUrl("/accounts/login/")}>
            Đăng nhập mạng xã hội / khóa bảo mật
          </a>
        </div>
      </div>
    </main>
  );
}

function Problems() {
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(query);
      setOffset(0);
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);
  const problems = useQuery({
    queryKey: ["problems", search, offset],
    queryFn: ({ signal }) => api.getProblems(search, offset, signal),
  });
  return (
    <main className="content">
      <div className="section-title">
        <div>
          <p className="eyebrow">LUYỆN TẬP</p>
          <h1>Danh sách bài tập</h1>
          <p className="muted">Chọn một bài và bắt đầu suy nghĩ.</p>
        </div>
        <input
          className="search"
          aria-label="Tìm bài tập"
          placeholder="Tìm theo mã hoặc tên..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      <ErrorMessage error={problems.error} />
      {problems.isPending && <p role="status">Đang tải bài tập...</p>}
      <div className="problem-grid">
        {problems.data?.results.map((problem) => (
          <Link
            className="problem-card"
            key={problem.code}
            to={`/problems/${problem.code}`}
          >
            <div className="problem-code">{problem.code}</div>
            <h2>{problem.name}</h2>
            <div className="problem-meta">
              <span>{problem.points} điểm</span>
              <span>{problem.time_limit} s</span>
            </div>
            <span className="arrow">↗</span>
          </Link>
        ))}
      </div>
      {problems.data && (
        <>
          <p className="muted">{problems.data.count} bài tập phù hợp</p>
          <div className="pagination">
            <button
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - 30))}
            >
              Trang trước
            </button>
            <button
              disabled={problems.data.nextOffset == null}
              onClick={() => setOffset(problems.data!.nextOffset!)}
            >
              Trang sau
            </button>
          </div>
        </>
      )}
    </main>
  );
}

function Statement({ html }: { html: string }) {
  const element = useRef<HTMLElement>(null);
  const clean = DOMPurify.sanitize(html, {
    ADD_TAGS: ["math"],
    FORBID_TAGS: ["style", "iframe", "object"],
  });
  useEffect(() => {
    let cancelled = false;
    import("katex/contrib/auto-render").then(({ default: renderMath }) => {
      if (!cancelled && element.current)
        renderMath(element.current, {
          throwOnError: false,
          trust: false,
          delimiters: [
            { left: "$$", right: "$$", display: true },
            { left: "\\[", right: "\\]", display: true },
            { left: "\\(", right: "\\)", display: false },
            { left: "$", right: "$", display: false },
          ],
        });
    });
    return () => {
      cancelled = true;
    };
  }, [clean]);
  return (
    <article
      className="statement"
      ref={element}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}

function ProblemDetail() {
  const { code = "" } = useParams();
  const problem = useQuery({
    queryKey: ["problem", code],
    queryFn: () => api.getProblem(code),
  });
  if (!problem.data)
    return (
      <main className="content">
        <ErrorMessage error={problem.error} />
        {problem.isPending && <p>Đang tải đề bài...</p>}
      </main>
    );
  const data = problem.data;
  return (
    <main className="content detail">
      <Link className="back" to="/problems">
        ← Tất cả bài tập
      </Link>
      <div className="detail-head">
        <div>
          <p className="eyebrow">{code}</p>
          <h1>{data.name}</h1>
        </div>
        <a href={api.legacyUrl(data.legacy_url)}>Mở trang bài tập đầy đủ</a>
      </div>
      <div className="detail-layout">
        <Statement
          html={data.statement || "<p>Đề bài đang được cập nhật.</p>"}
        />
        <aside>
          <div className="stats">
            <strong>Thông tin</strong>
            <div>
              <span>Thời gian</span>
              <b>{data.time_limit} s</b>
            </div>
            <div>
              <span>Bộ nhớ</span>
              <b>{data.memory_limit / 1024} MiB</b>
            </div>
            <div>
              <span>Điểm</span>
              <b>{data.points}</b>
            </div>
          </div>
        </aside>
      </div>
      <SubmitForm key={code} code={code} />
    </main>
  );
}

function SubmitForm({ code }: { code: string }) {
  const session = useSession();
  const user = session.data?.user?.id;
  const authenticated = session.data?.status === "authenticated";
  const options = useQuery({
    queryKey: ["submit-options", code, user],
    queryFn: () => api.getSubmitOptions(code),
    enabled: authenticated,
    retry: false,
  });
  const [source, setSource] = useState("");
  const [language, setLanguage] = useState("");
  const attempt = useRef<{ payload: string; key: string } | null>(null);
  const locked = useRef(false);
  const navigate = useNavigate();
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: (key: string) => api.submitProblem(code, source, language, key),
    retry: false,
    onSuccess: (submission) => {
      client.invalidateQueries({ queryKey: ["submissions"] });
      navigate(`/submissions/${submission.id}`);
    },
    onSettled: () => {
      locked.current = false;
    },
  });
  useEffect(() => {
    if (options.data)
      setLanguage(
        options.data.languages.find(
          (lang) => lang.key === options.data.default_language,
        )?.key ||
          options.data.languages[0]?.key ||
          "",
      );
  }, [options.data]);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (locked.current) return;
    const payload = JSON.stringify({ user, code, source, language });
    if (attempt.current?.payload !== payload)
      attempt.current = { payload, key: crypto.randomUUID() };
    locked.current = true;
    mutation.mutate(attempt.current.key);
  };
  if (!authenticated)
    return (
      <section className="submit-form">
        <Link to="/login">Đăng nhập để nộp bài →</Link>
      </section>
    );
  return (
    <section className="submit-form">
      <h2>Nộp lời giải</h2>
      <ErrorMessage error={options.error || mutation.error} />
      {options.isPending ? (
        <p>Đang tải ngôn ngữ...</p>
      ) : !options.data ? null : !options.data.languages.length ? (
        <p>Chưa có máy chấm sẵn sàng cho bài này.</p>
      ) : (
        <form onSubmit={submit}>
          <label>
            Ngôn ngữ
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              disabled={mutation.isPending}
              required
            >
              {options.data.languages.map((lang) => (
                <option key={lang.key} value={lang.key}>
                  {lang.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Mã nguồn
            <textarea
              value={source}
              onChange={(e) => setSource(e.target.value)}
              rows={14}
              maxLength={options.data.max_source_length}
              spellCheck={false}
              autoCapitalize="off"
              disabled={mutation.isPending}
              required
            />
          </label>
          <button
            className="button"
            disabled={mutation.isPending || !source.trim() || !language}
          >
            {mutation.isPending ? "Đang gửi..." : "Nộp bài"}
          </button>
          <p className="muted">
            Nếu mất kết nối, bấm nộp lại cùng lời giải để kiểm tra lần gửi
            trước.
          </p>
        </form>
      )}
    </section>
  );
}

function SubmissionDetail() {
  const { id = "" } = useParams();
  const session = useSession();
  const result = useQuery({
    queryKey: ["submission", id, session.data?.user?.id],
    queryFn: () => api.getSubmission(id),
    enabled: session.data?.status === "authenticated",
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.finished || query.state.error
        ? false
        : Math.min(1500 * 1.3 ** query.state.dataUpdateCount, 10000),
    refetchIntervalInBackground: false,
  });
  if (session.isPending)
    return <main className="content">Đang kiểm tra đăng nhập...</main>;
  if (session.data?.status !== "authenticated")
    return (
      <main className="content">
        <Link to="/login">Đăng nhập để xem bài nộp</Link>
        <ErrorMessage error={session.error} />
      </main>
    );
  const data = result.data;
  return (
    <main className="content">
      <h1>Bài nộp #{id}</h1>
      <ErrorMessage error={result.error} />
      {result.error && (
        <button className="button" onClick={() => result.refetch()}>
          Thử lại
        </button>
      )}
      {result.isPending && <p>Đang tải kết quả...</p>}
      {data && (
        <>
          <Link to={`/problems/${data.problem.code}`}>{data.problem.name}</Link>
          <div
            className={`verdict ${data.result === "AC" ? "accepted" : ""}`}
            role="status"
          >
            {data.result || data.status_label}
          </div>
          <p className="muted">
            {data.finished
              ? "Đã hoàn tất"
              : "Kết quả tự cập nhật khi máy chấm xử lý."}
          </p>
          <div className="stats">
            <div>
              <span>Thời gian</span>
              <b>{data.time ?? "-"} s</b>
            </div>
            <div>
              <span>Bộ nhớ</span>
              <b>{data.memory == null ? "-" : data.memory / 1024} MiB</b>
            </div>
            <div>
              <span>Điểm</span>
              <b>{data.points ?? "-"}</b>
            </div>
          </div>
          {data.message && <p role="alert">{data.message}</p>}
          {data.compile_error && (
            <pre className="compiler-error">{data.compile_error}</pre>
          )}
          <p>
            <a href={api.legacyUrl(data.legacy_url)}>
              Xem chi tiết, mã nguồn và thao tác bài nộp
            </a>
          </p>
        </>
      )}
    </main>
  );
}

function Submissions() {
  const [offset, setOffset] = useState(0);
  const session = useSession();
  const list = useQuery({
    queryKey: ["submissions", session.data?.user?.id, offset],
    queryFn: () => api.getSubmissions(offset),
    enabled: session.data?.status === "authenticated",
    retry: false,
  });
  return (
    <main className="content">
      <h1>Bài đã nộp</h1>
      <ErrorMessage error={list.error || session.error} />
      {session.data?.status !== "authenticated" ? (
        <Link to="/login">Đăng nhập để xem bài nộp</Link>
      ) : (
        <>
          {list.isPending && <p>Đang tải...</p>}
          {list.data?.results.map((sub) => (
            <Link
              className="submission-row"
              key={sub.id}
              to={`/submissions/${sub.id}`}
            >
              <span>
                #{sub.id} · {sub.problem.name}
              </span>
              <strong>{sub.result || sub.status_label}</strong>
            </Link>
          ))}
          {list.data?.count === 0 && <p>Chưa có bài nộp luyện tập.</p>}
          <div className="pagination">
            <button
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - 25))}
            >
              Trang trước
            </button>
            <button
              disabled={list.data?.next_offset == null}
              onClick={() => setOffset(list.data!.next_offset!)}
            >
              Trang sau
            </button>
          </div>
        </>
      )}
    </main>
  );
}

function LegacyPage() {
  return (
    <main className="content">
      <h1>Trang này thuộc giao diện đầy đủ</h1>
      <p>Kỳ thi, cộng đồng và quản lý tài khoản tiếp tục hoạt động tại đây.</p>
      <a
        className="button"
        href={api.legacyUrl(window.location.pathname + window.location.search)}
      >
        Mở trang đầy đủ →
      </a>
    </main>
  );
}
